import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { format, parseISO, subMonths } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { MdArrowForward } from 'react-icons/md';
import './Dashboard.css';

// Bucket an expense category by its budget_group. Income and transfer categories
// are explicitly excluded — they don't belong in the Fixed/Flexible/Non-Monthly view.
const bucketOfCategory = (cat) => {
  if (!cat) return null;
  if (cat.type === 'income' || cat.type === 'transfer') return null;
  const g = (cat.budget_group || 'flexible').toLowerCase();
  if (g === 'fixed') return 'fixed';
  if (g === 'non_monthly') return 'non_monthly';
  return 'flexible';
};

// Aggregate planned (from budgets) and actual (from summary) by group key.
// Uses the category list as the source of truth for group membership so that
// even unbudgeted categories with spending (e.g. Travel) show up under their group.
const computeGroupTotals = (categories, budgets, summary) => {
  const totals = {
    fixed: { planned: 0, spent: 0 },
    flexible: { planned: 0, spent: 0 },
    non_monthly: { planned: 0, spent: 0 },
  };
  const budgetByCat = new Map(budgets.map((b) => [b.category_id, b]));
  const summaryByCat = new Map(summary.map((s) => [s.category_id, s]));
  categories.forEach((cat) => {
    const bucket = bucketOfCategory(cat);
    if (!bucket) return;
    const planned = parseFloat(budgetByCat.get(cat.id)?.amount || 0);
    const spent = parseFloat(summaryByCat.get(cat.id)?.total_expenses || 0);
    totals[bucket].planned += planned;
    totals[bucket].spent += spent;
  });
  return totals;
};

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const fmtMoneyFull = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const monthRange = (yyyymm) => {
  const [year, month] = yyyymm.split('-');
  const yearNum = parseInt(year);
  const monthNum = parseInt(month);
  const lastDay = new Date(yearNum, monthNum, 0).getDate();
  return {
    start_date: `${year}-${month}-01`,
    end_date: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
  };
};

const BudgetGroupBar = ({ label, planned, spent }) => {
  const remaining = planned - spent;
  const over = spent > planned;
  // When there's no budget but spending exists, fill the bar fully in red so
  // the over-budget signal is visible.
  const pct = planned > 0
    ? Math.min((spent / planned) * 100, 100)
    : (spent > 0 ? 100 : 0);
  return (
    <div className="bg-row">
      <div className="bg-row-head">
        <span className="bg-row-label">{label}</span>
        <span className="bg-row-planned">
          {planned === 0 && spent === 0 ? 'No budget' : `${fmtMoney(planned)} planned`}
        </span>
      </div>
      <div className="bg-bar">
        <div
          className={`bg-bar-fill ${over ? 'over' : ''}`}
          style={{ width: `${pct}%` }}
        ></div>
      </div>
      <div className="bg-row-foot">
        <span>{fmtMoneyFull(spent)} spent</span>
        <span className={remaining < 0 ? 'neg' : ''}>
          {planned === 0 && spent === 0
            ? '—'
            : `${fmtMoneyFull(Math.abs(remaining))} ${remaining < 0 ? 'over' : 'remaining'}`}
        </span>
      </div>
    </div>
  );
};

const Dashboard = ({ onNavigate }) => {
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [summary, setSummary] = useState([]);
  const [spendingThis, setSpendingThis] = useState([]);
  const [spendingLast, setSpendingLast] = useState([]);
  const [recentTx, setRecentTx] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const current = monthRange(selectedMonth);
        const lastMonthDate = subMonths(parseISO(`${selectedMonth}-01`), 1);
        const lastYm = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;
        const last = monthRange(lastYm);

        const [catRes, budgetsRes, summaryRes, txRes, txLastRes, recentRes] = await Promise.all([
          api.get('/api/categories'),
          api.get('/api/budgets/current', { params: current }),
          api.get('/api/transactions/summary', { params: current }),
          api.get('/api/transactions', { params: { ...current, limit: 500 } }),
          api.get('/api/transactions', { params: { ...last, limit: 500 } }),
          api.get('/api/transactions', { params: { ...current, limit: 6 } }),
        ]);

        setCategories(catRes.data);
        setBudgets(budgetsRes.data);
        setSummary(summaryRes.data);
        setSpendingThis(txRes.data);
        setSpendingLast(txLastRes.data);
        setRecentTx(recentRes.data.slice(0, 6));
      } catch (e) {
        console.error('Dashboard fetch failed', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [selectedMonth]);

  if (loading) return <div className="loading">Loading dashboard...</div>;

  const totals = computeGroupTotals(categories, budgets, summary);
  const fixed = totals.fixed;
  const flexible = totals.flexible;
  const nonMonthly = totals.non_monthly;
  const hasAnyBudgetData = budgets.length > 0 || summary.some((s) => parseFloat(s.total_expenses) > 0);
  // Real spending excludes transfers (paired moves between own accounts).
  const isReal = (t) => t.category_type !== 'transfer';

  const totalSpent =
    spendingThis
      .filter((t) => isReal(t) && parseFloat(t.amount) < 0)
      .reduce((s, t) => s + Math.abs(parseFloat(t.amount)), 0);

  const buildCumulative = (txs) => {
    const byDay = {};
    txs.forEach((t) => {
      if (!isReal(t)) return;
      const amt = parseFloat(t.amount);
      if (amt >= 0) return;
      const day = new Date(t.transaction_date).getDate();
      byDay[day] = (byDay[day] || 0) + Math.abs(amt);
    });
    let cum = 0;
    const days = Array.from({ length: 31 }, (_, i) => i + 1);
    return days.map((d) => {
      cum += byDay[d] || 0;
      return { day: d, total: Math.round(cum * 100) / 100 };
    });
  };

  const thisSeries = buildCumulative(spendingThis);
  const lastSeries = buildCumulative(spendingLast);
  const merged = thisSeries.map((p, i) => ({
    day: `Day ${p.day}`,
    'This month': p.total,
    'Last month': lastSeries[i]?.total ?? 0,
  }));

  return (
    <div className="dashboard">
      <div className="dashboard-grid">
        <div className="card">
          <div className="panel-head">
            <h2>Budget <span className="panel-head-sub">{format(parseISO(`${selectedMonth}-01`), 'MMMM yyyy')}</span></h2>
            <button className="panel-link" onClick={() => onNavigate?.('budgets')}>
              View budget <MdArrowForward />
            </button>
          </div>
          {!hasAnyBudgetData ? (
            <p className="empty-state">No budgets yet. Add some in the Budget tab.</p>
          ) : (
            <div className="bg-list">
              <BudgetGroupBar label="Fixed" planned={fixed.planned} spent={fixed.spent} />
              <BudgetGroupBar label="Flexible" planned={flexible.planned} spent={flexible.spent} />
              <BudgetGroupBar label="Non-Monthly" planned={nonMonthly.planned} spent={nonMonthly.spent} />
            </div>
          )}
        </div>

        <div className="card">
          <div className="panel-head">
            <h2>Spending <span className="panel-head-sub">{fmtMoneyFull(totalSpent)} this month</span></h2>
            <button className="panel-link" onClick={() => onNavigate?.('transactions')}>
              View transactions <MdArrowForward />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={merged} margin={{ top: 10, right: 15, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#eee" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9a9a96' }} interval={4} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => fmtMoney(v)} tick={{ fontSize: 11, fill: '#9a9a96' }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => fmtMoneyFull(v)} />
              <Line type="monotone" dataKey="Last month" stroke="#9a9a96" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="This month" stroke="#f5793b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="legend-row">
            <span><span className="legend-dot last"></span>Last month</span>
            <span><span className="legend-dot this"></span>This month</span>
          </div>
        </div>

        <div className="card panel-full">
          <div className="panel-head">
            <h2>Transactions <span className="panel-head-sub">Most recent</span></h2>
            <button className="panel-link" onClick={() => onNavigate?.('transactions')}>
              View all <MdArrowForward />
            </button>
          </div>
          {recentTx.length === 0 ? (
            <p className="empty-state">No transactions this month.</p>
          ) : (
            <div className="recent-tx">
              {recentTx.map((t) => {
                const amt = parseFloat(t.amount);
                const expense = amt < 0;
                return (
                  <div key={t.id} className="recent-tx-row">
                    <div className="recent-tx-icon" style={{ background: (t.color || '#e9e9e6') + '22', color: t.color || '#6b6b6b' }}>
                      {t.icon || '•'}
                    </div>
                    <div className="recent-tx-main">
                      <div className="recent-tx-name">{t.merchant_name || t.description || 'Transaction'}</div>
                      <div className="recent-tx-meta">
                        {t.category_name || 'Uncategorized'}
                        {t.account_name && <> · {t.account_name}</>}
                      </div>
                    </div>
                    <div className="recent-tx-right">
                      <div className={`recent-tx-amount ${expense ? 'expense' : 'income'}`}>
                        {expense ? '-' : '+'}{fmtMoneyFull(Math.abs(amt))}
                      </div>
                      <div className="recent-tx-date">{format(new Date(t.transaction_date), 'MMM d')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
